"use client";

import { useEffect, useRef, useState } from "react";
import type { JSX } from "react";
import { useGameStore } from "@/stores/game-store";
import { DamageNumber } from "./DamageNumber";
import { onJuice, hitStopRemaining, juiceEnabled, type JuiceEvent } from "@/engine/juice";

export type EffectKind =
  | "attack"
  | "hurt-flash"
  | "faint-dissolve"
  | "catch-shake"
  | "evolution-burst"
  | "levelup-sparkle";

export type EffectRequest = {
  id: string;
  kind: EffectKind;
  type?: string;
  origin: { x: number; y: number };
  target?: { x: number; y: number };
  onComplete?: () => void;
};

type Props = {
  effects: EffectRequest[];
  className?: string;
};

type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  gravity: number;
  size: number;
  color: string;
  bornAt: number;
  life: number; // ms
};

type ActiveEffect = {
  id: string;
  kind: EffectKind;
  startedAt: number;
  duration: number;
  origin: { x: number; y: number };
  target?: { x: number; y: number };
  type?: string;
  onComplete?: () => void;
  completed: boolean;
};

const TYPE_COLORS: Record<string, string> = {
  sales: "#8B4513",
  tech: "#2F4F4F",
  finance: "#8B7355",
  creative: "#6B3D6B",
  ops: "#4A6741",
  hr: "#7B5E3D",
  normal: "#888888",
};

const MAX_PARTICLES = 200;

// A live floating damage number rendered as an HTML overlay (sibling of the
// particle canvas). Positions are percentages of the panel so they track the
// sprite anchors at any resolution.
type FloatingNumber = {
  id: string;
  amount: number;
  target: "player" | "opponent";
  // Horizontal nudge (px) so stacked concurrent hits don't fully overlap.
  offsetX: number;
};

// Approximate sprite-center anchors within the 16:9 panel.
// Opponent sits top-right (BattleArena: top 6% / right 6%, sprite under card);
// player sits bottom-left (bottom 18% / left 6%).
const TARGET_ANCHOR: Record<FloatingNumber["target"], { left: string; top: string }> = {
  opponent: { left: "82%", top: "30%" },
  player: { left: "20%", top: "62%" },
};

let floatingSeq = 0;

function typeColor(type?: string): string {
  if (!type) return "#aaaaaa";
  return TYPE_COLORS[type] ?? "#aaaaaa";
}

function durationFor(kind: EffectKind): number {
  switch (kind) {
    case "attack":
      return 400;
    case "hurt-flash":
      return 300;
    case "faint-dissolve":
      return 600;
    case "catch-shake":
      return 1200;
    case "evolution-burst":
      return 1500;
    case "levelup-sparkle":
      return 800;
  }
}

export function BattleEffects(props: Props): JSX.Element {
  const { effects, className } = props;
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const particlesRef = useRef<Particle[]>([]);
  const activeRef = useRef<ActiveEffect[]>([]);
  const seenIdsRef = useRef<Set<string>>(new Set());
  const rafRef = useRef<number | null>(null);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });

  // Crit screen-flash: a wall-clock start time + duration, set from the juice
  // bus. The RAF draws a fading full-panel flash while active. Gold, not white,
  // to stay on-palette and read as "impact" rather than a camera blowout.
  const critFlashRef = useRef<{ at: number; dur: number } | null>(null);

  // ── Damage numbers derived from opponent HP deltas in the battle store ──
  // The store only tracks opponent HP, so the amount is the drop between
  // renders. Effectiveness/crit are not persisted to the store, so numbers
  // render "normal" (white). See report for the fields that would unlock
  // color-coding and player-side numbers.
  const opponentHp = useGameStore((s) => s.battle.opponentHpCurrent);
  const playerHp = useGameStore((s) => s.battle.playerHpCurrent);
  const battleActive = useGameStore((s) => s.battle.active);
  const prevHpRef = useRef<number>(opponentHp);
  const prevPlayerHpRef = useRef<number>(playerHp);
  const [floaters, setFloaters] = useState<FloatingNumber[]>([]);

  useEffect(() => {
    // Reset the baseline when a battle starts/ends so the opening fill (0 → max)
    // and teardown don't spawn a stray number.
    if (!battleActive) {
      prevHpRef.current = opponentHp;
      return;
    }
    const prev = prevHpRef.current;
    prevHpRef.current = opponentHp;
    const delta = prev - opponentHp;
    if (delta <= 0) return; // heal / fill / no change — no damage number
    const seq = floatingSeq++;
    const id = `dmg-${seq}`;
    // Alternate a small left/right offset so rapid concurrent hits read apart.
    const offsetX = ((seq % 3) - 1) * 14;
    setFloaters((cur) => [...cur, { id, amount: delta, target: "opponent", offsetX }]);
  }, [opponentHp, battleActive]);

  // Mirror of the opponent block for player-side damage numbers (spawned at the
  // player anchor). The bridge now mirrors player-target damage into the store,
  // so the same prev-vs-current delta drives this.
  useEffect(() => {
    if (!battleActive) {
      prevPlayerHpRef.current = playerHp;
      return;
    }
    const prev = prevPlayerHpRef.current;
    prevPlayerHpRef.current = playerHp;
    const delta = prev - playerHp;
    if (delta <= 0) return;
    const seq = floatingSeq++;
    const id = `dmg-${seq}`;
    const offsetX = ((seq % 3) - 1) * 14;
    setFloaters((cur) => [...cur, { id, amount: delta, target: "player", offsetX }]);
  }, [playerHp, battleActive]);

  const removeFloater = (id: string) =>
    setFloaters((cur) => cur.filter((f) => f.id !== id));

  // Subscribe to the juice bus for the crit screen-flash cue. Screen shake,
  // camera kick/zoom and hit-stop are handled elsewhere (BattleArena transform
  // + this component's particle freeze); here we only latch the flash.
  useEffect(() => {
    const off = onJuice((e: JuiceEvent) => {
      if (!juiceEnabled()) return;
      if (e.kind === "hit" && e.crit) {
        critFlashRef.current = { at: performance.now(), dur: 220 };
      }
    });
    return off;
  }, []);

  // Push a single effect request into the active queue (idempotent on id) and
  // fire its one-shot side effects. Shared by the prop-driven intake and the
  // store-driven faint trigger.
  function ingestEffect(e: EffectRequest, now: number) {
    if (seenIdsRef.current.has(e.id)) return;
    seenIdsRef.current.add(e.id);
    const duration = durationFor(e.kind);
    activeRef.current.push({
      id: e.id,
      kind: e.kind,
      startedAt: now,
      duration,
      origin: e.origin,
      target: e.target,
      type: e.type,
      onComplete: e.onComplete,
      completed: false,
    });

    // Screen shake on hurt-flash
    if (e.kind === "hurt-flash" && containerRef.current) {
      const el = containerRef.current;
      el.classList.add("battle-shake");
      window.setTimeout(() => el.classList.remove("battle-shake"), 300);
    }

    // Faint dissolve: add CSS class to container
    if (e.kind === "faint-dissolve" && containerRef.current) {
      const el = containerRef.current;
      el.classList.add("battle-faint-dissolve");
      window.setTimeout(() => el.classList.remove("battle-faint-dissolve"), 600);
    }

    // Catch shake: add CSS class for 3-pulse ball shake
    if (e.kind === "catch-shake" && containerRef.current) {
      const el = containerRef.current;
      el.classList.add("battle-catch-shake");
      window.setTimeout(() => el.classList.remove("battle-catch-shake"), 1200);
    }

    spawnInitialParticles(e, now);
  }

  // Sync incoming effect requests into the active queue (only newly seen ids).
  useEffect(() => {
    const now = performance.now();
    for (const e of effects) {
      ingestEffect(e, now);
    }
  }, [effects]);

  // Faint-dissolve when a side's store HP transitions to 0. Edge-guarded on the
  // prev>0 → now===0 transition so it fires exactly once per faint.
  const faintFiredRef = useRef<{ player: boolean; opponent: boolean }>({
    player: false,
    opponent: false,
  });
  useEffect(() => {
    if (!battleActive) {
      faintFiredRef.current = { player: false, opponent: false };
      return;
    }
    const { w, h } = sizeRef.current;
    if (w <= 0 || h <= 0) return; // canvas not measured yet
    const fire = (side: "player" | "opponent") => {
      if (faintFiredRef.current[side]) return;
      faintFiredRef.current[side] = true;
      const anchor = TARGET_ANCHOR[side];
      const origin = {
        x: (parseFloat(anchor.left) / 100) * w,
        y: (parseFloat(anchor.top) / 100) * h,
      };
      ingestEffect(
        { id: `faint-${side}-${floatingSeq++}`, kind: "faint-dissolve", origin },
        performance.now(),
      );
    };
    if (opponentHp <= 0) fire("opponent");
    if (playerHp <= 0) fire("player");
    // Re-arm a side once its HP is restored (switch-in / new battle).
    if (opponentHp > 0) faintFiredRef.current.opponent = false;
    if (playerHp > 0) faintFiredRef.current.player = false;
  }, [opponentHp, playerHp, battleActive]);

  function pushParticle(p: Particle) {
    const arr = particlesRef.current;
    arr.push(p);
    if (arr.length > MAX_PARTICLES) {
      arr.splice(0, arr.length - MAX_PARTICLES);
    }
  }

  function spawnInitialParticles(e: EffectRequest, now: number) {
    switch (e.kind) {
      case "attack": {
        const color = typeColor(e.type);
        const tx = e.target?.x ?? e.origin.x;
        const ty = e.target?.y ?? e.origin.y;
        const dx = tx - e.origin.x;
        const dy = ty - e.origin.y;
        const baseAngle = Math.atan2(dy, dx);
        const dist = Math.hypot(dx, dy);
        // Travel ~dist over ~350ms; vx,vy in px/ms.
        const travelTime = 350;
        for (let i = 0; i < 12; i++) {
          const spread = (Math.random() - 0.5) * 0.8;
          const angle = baseAngle + spread;
          const speed = (dist / travelTime) * (0.8 + Math.random() * 0.6);
          pushParticle({
            x: e.origin.x,
            y: e.origin.y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            gravity: 0.0015, // px/ms^2
            size: 2 + (Math.random() < 0.5 ? 0 : 1),
            color,
            bornAt: now,
            life: 400,
          });
        }
        break;
      }
      case "evolution-burst": {
        const gold = "#f0c44a";
        for (let i = 0; i < 60; i++) {
          const angle = (i / 60) * Math.PI * 2 + Math.random() * 0.1;
          const speed = 0.08 + Math.random() * 0.12; // px/ms
          pushParticle({
            x: e.origin.x,
            y: e.origin.y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            gravity: 0,
            size: 2 + (Math.random() < 0.4 ? 1 : 0),
            color: gold,
            bornAt: now,
            life: 1500,
          });
        }
        break;
      }
      case "faint-dissolve": {
        // Particles are spawned over time in the loop; nothing to pre-spawn.
        break;
      }
      case "levelup-sparkle": {
        // Sparkles are drawn directly each frame; no particle backing needed.
        break;
      }
      default:
        break;
    }
  }

  // Continuous spawners + per-frame visuals
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return;

    const ro = new ResizeObserver(() => {
      const rect = container.getBoundingClientRect();
      const dpr = Math.max(1, Math.min(2, window.devicePixelRatio || 1));
      sizeRef.current = { w: rect.width, h: rect.height, dpr };
      canvas.width = Math.max(1, Math.floor(rect.width * dpr));
      canvas.height = Math.max(1, Math.floor(rect.height * dpr));
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
    });
    ro.observe(container);

    let lastT = performance.now();
    let lastFaintSpawn = 0;
    let lastSparkleSpawn = 0;

    const tick = (now: number) => {
      const dt = Math.min(64, now - lastT);
      lastT = now;

      // Hit-stop: while frozen we keep painting (so the frame visibly holds)
      // but pause simulation — no particle integration, no new spawns, and we
      // freeze the effect clocks by sliding their startedAt forward by dt. This
      // makes a crit feel like a hard "thunk" without stalling the turn (engine
      // logic already ran). `frozen` is always false when juice is disabled
      // because requestHitStop() no-ops in that case.
      const frozen = hitStopRemaining(now) > 0;

      const { w, h, dpr } = sizeRef.current;
      // Pixel-art: no smoothing, integer coords.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.clearRect(0, 0, w, h);

      if (frozen) {
        // Hold every effect + particle clock so animations resume exactly where
        // they paused once the freeze lifts (no age-out, no drift mid-freeze).
        for (const eff of activeRef.current) eff.startedAt += dt;
        for (const p of particlesRef.current) p.bornAt += dt;
      }

      // Continuous spawners for time-based effects. Suppressed while frozen.
      for (const eff of frozen ? [] : activeRef.current) {
        const t = now - eff.startedAt;
        if (t > eff.duration) continue;

        if (eff.kind === "faint-dissolve" && now - lastFaintSpawn > 30) {
          // Vertical falling rain at origin: spawn a few greyish pixels.
          for (let i = 0; i < 3; i++) {
            pushParticle({
              x: eff.origin.x + (Math.random() - 0.5) * 32,
              y: eff.origin.y - 16 + Math.random() * 8,
              vx: 0,
              vy: 0.08 + Math.random() * 0.05,
              gravity: 0,
              size: 2,
              color: "#6b6258",
              bornAt: now,
              life: 600,
            });
          }
          lastFaintSpawn = now;
        }

        if (eff.kind === "levelup-sparkle" && now - lastSparkleSpawn > 80) {
          // Yellow 3x3 cross sparkles drifting up — store as a "particle"
          // with size 3, color yellow, slight horizontal jitter.
          for (let i = 0; i < 2; i++) {
            pushParticle({
              x: eff.origin.x + (Math.random() - 0.5) * 28,
              y: eff.origin.y + (Math.random() - 0.5) * 12,
              vx: (Math.random() - 0.5) * 0.02,
              vy: -0.05 - Math.random() * 0.04,
              gravity: 0,
              size: 3,
              color: "#f5d24a",
              bornAt: now,
              life: 800,
            });
          }
          lastSparkleSpawn = now;
        }
      }

      // Update + draw particles.
      const live: Particle[] = [];
      for (const p of particlesRef.current) {
        const age = now - p.bornAt;
        if (age >= p.life) continue;
        // While frozen, hold positions (bornAt was already slid forward above).
        if (!frozen) {
          p.vy += p.gravity * dt;
          p.x += p.vx * dt;
          p.y += p.vy * dt;
        }

        const lifeFrac = age / p.life;
        const alpha = Math.max(0, 1 - lifeFrac);
        ctx.globalAlpha = alpha;
        ctx.fillStyle = p.color;

        const ix = Math.round(p.x);
        const iy = Math.round(p.y);
        if (p.size === 3) {
          // Yellow 3x3 cross sparkle (plus shape).
          ctx.fillRect(ix - 1, iy, 3, 1);
          ctx.fillRect(ix, iy - 1, 1, 3);
        } else {
          ctx.fillRect(ix, iy, p.size, p.size);
        }
        live.push(p);
      }
      particlesRef.current = live;
      ctx.globalAlpha = 1;

      // Per-effect direct overlays (don't need particles).
      for (const eff of activeRef.current) {
        const t = now - eff.startedAt;
        if (t > eff.duration) continue;
        const frac = t / eff.duration;

        if (eff.kind === "hurt-flash") {
          const a = 0.4 * (1 - frac);
          ctx.globalAlpha = a;
          ctx.fillStyle = "#c0392b";
          ctx.fillRect(0, 0, w, h);
          ctx.globalAlpha = 1;
        }

        if (eff.kind === "evolution-burst") {
          // White vertical light beam at origin — pulses, fades.
          const pulse = 0.6 + 0.4 * Math.sin(frac * Math.PI * 4);
          const beamW = Math.max(2, Math.round(6 * pulse));
          ctx.globalAlpha = (1 - frac) * 0.9;
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(
            Math.round(eff.origin.x - beamW / 2),
            0,
            beamW,
            h,
          );
          ctx.globalAlpha = 1;
        }
      }

      // Crit screen-flash: a quick gold wash over the whole panel that fades
      // out. On-palette (game gold), capped low alpha so it punctuates a crit
      // without blinding. Frozen frames still paint it (held by hit-stop).
      const flash = critFlashRef.current;
      if (flash) {
        const ft = now - flash.at;
        if (ft >= flash.dur) {
          critFlashRef.current = null;
        } else {
          const a = 0.5 * (1 - ft / flash.dur);
          ctx.globalAlpha = a;
          ctx.fillStyle = "#c5a83a";
          ctx.fillRect(0, 0, w, h);
          ctx.globalAlpha = 1;
        }
      }

      // Retire completed effects (fire onComplete once).
      const stillActive: ActiveEffect[] = [];
      for (const eff of activeRef.current) {
        const t = now - eff.startedAt;
        if (t >= eff.duration) {
          if (!eff.completed) {
            eff.completed = true;
            try {
              eff.onComplete?.();
            } catch {
              /* ignore consumer errors */
            }
          }
          continue;
        }
        stillActive.push(eff);
      }
      activeRef.current = stillActive;

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      ro.disconnect();
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes battle-shake-kf {
          0%   { transform: translateX(0); }
          15%  { transform: translateX(-4px); }
          30%  { transform: translateX(4px); }
          45%  { transform: translateX(-4px); }
          60%  { transform: translateX(4px); }
          75%  { transform: translateX(-2px); }
          90%  { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }
        @keyframes battle-faint-dissolve-kf {
          0%   { opacity: 1; transform: translateY(0); }
          100% { opacity: 0; transform: translateY(12px); }
        }
        @keyframes battle-catch-shake-kf {
          0%   { transform: translateX(0) rotate(0deg); }
          8%   { transform: translateX(-4px) rotate(-3deg); }
          16%  { transform: translateX(4px) rotate(3deg); }
          24%  { transform: translateX(-4px) rotate(-3deg); }
          32%  { transform: translateX(4px) rotate(3deg); }
          40%  { transform: translateX(0) rotate(0deg); }
          55%  { transform: translateX(-4px) rotate(-3deg); }
          63%  { transform: translateX(4px) rotate(3deg); }
          71%  { transform: translateX(-4px) rotate(-3deg); }
          79%  { transform: translateX(4px) rotate(3deg); }
          87%  { transform: translateX(0) rotate(0deg); }
          93%  { transform: translateX(-3px) rotate(-2deg); }
          97%  { transform: translateX(3px) rotate(2deg); }
          100% { transform: translateX(0) rotate(0deg); }
        }
        .battle-shake {
          animation: battle-shake-kf 300ms steps(1, end);
        }
        .battle-faint-dissolve {
          animation: battle-faint-dissolve-kf 600ms ease-in forwards;
        }
        .battle-catch-shake {
          animation: battle-catch-shake-kf 1200ms ease-in-out;
        }
      `}</style>
      <div
        ref={containerRef}
        className={className}
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
        }}
      >
        <canvas
          ref={canvasRef}
          style={{
            width: "100%",
            height: "100%",
            imageRendering: "pixelated",
            display: "block",
          }}
        />

        {/* Floating damage numbers — HTML overlay above the particle canvas.
            Multiple concurrent numbers are supported; each removes itself on
            animation end. */}
        {floaters.map((f) => {
          const anchor = TARGET_ANCHOR[f.target];
          const left = f.offsetX === 0 ? anchor.left : `calc(${anchor.left} + ${f.offsetX}px)`;
          return (
            <DamageNumber
              key={f.id}
              amount={f.amount}
              x={left}
              y={anchor.top}
              onComplete={() => removeFloater(f.id)}
            />
          );
        })}
      </div>
    </>
  );
}
